import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import { approveBroadcast, rejectBroadcast, suspendBroadcast } from "./pendingBroadcastsApi";
import UserInfoModal from "../../../components/Common/UserInfoModal";

const BroadcastDetailModal = ({ isOpen, toggle, broadcast, onUpdate }) => {
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setRejectReason("");
      setSuspendReason("");
      setApproveModal(false);
      setRejectModal(false);
      setSuspendModal(false);
    }
  }, [isOpen]);

  const handleViewUserInfo = (userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  };

  const handleApprove = async () => {
    if (!broadcast || !broadcast.broadcast_id) {
      alert("Broadcast ID is missing");
      return;
    }

    setActionLoading(true);
    try {
      await approveBroadcast(broadcast.broadcast_id);
      setApproveModal(false);
      toggle();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error approving broadcast:", error);
      alert(error.response?.data?.message || error.message || "Failed to approve broadcast");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    if (!broadcast || !broadcast.broadcast_id) {
      alert("Broadcast ID is missing");
      return;
    }

    setActionLoading(true);
    try {
      await rejectBroadcast({
        broadcast_id: broadcast.broadcast_id,
        adminExpirationText: rejectReason.trim(),
      });
      setRejectModal(false);
      setRejectReason("");
      toggle();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error rejecting broadcast:", error);
      alert(error.response?.data?.message || error.message || "Failed to reject broadcast");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      alert("Please provide a suspension reason");
      return;
    }

    if (!broadcast || !broadcast.broadcast_id) {
      alert("Broadcast ID is missing");
      return;
    }

    setActionLoading(true);
    try {
      await suspendBroadcast({
        broadcast_id: broadcast.broadcast_id,
        adminExpirationText: suspendReason.trim(),
      });
      setSuspendModal(false);
      setSuspendReason("");
      toggle();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error suspending broadcast:", error);
      alert(error.response?.data?.message || error.message || "Failed to suspend broadcast");
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!broadcast) return null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered size="lg" className="broadcast-detail-modal">
        <ModalHeader className="bg-warning text-white" toggle={toggle}>
          <div className="d-flex align-items-center">
            <i className="mdi mdi-broadcast me-2"></i>
            <span>Broadcast Details</span>
          </div>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
          {/* Broadcast Information */}
          <Card className="mb-3 shadow-sm">
            <CardBody className="p-3">
              <h6 className="mb-3 fw-bold">
                <i className="mdi mdi-information me-2"></i>Broadcast Information
              </h6>
              <Row className="g-3">
                <Col md={6}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">Broadcast ID</label>
                    <div className="font-monospace small">{broadcast.broadcast_id}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">Status</label>
                    <div>
                      <Badge color="warning" className="px-2 py-1">Pending</Badge>
                    </div>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">Title</label>
                    <div className="fw-semibold">{broadcast.title || "N/A"}</div>
                  </div>
                </Col>
                {broadcast.description && (
                  <Col xs={12}>
                    <div className="mb-2">
                      <label className="form-label text-muted small mb-1">Description</label>
                      <div className="p-2 bg-light rounded border" style={{ whiteSpace: "pre-wrap" }}>
                        {broadcast.description}
                      </div>
                    </div>
                  </Col>
                )}
                <Col md={6}>
                  <div className="mb-2">
                    <label className="form-label text-muted small mb-1">Created At</label>
                    <div className="small">{formatDate(broadcast.createdAt)}</div>
                  </div>
                </Col>
                {broadcast.moderation_decision && (
                  <Col md={6}>
                    <div className="mb-2">
                      <label className="form-label text-muted small mb-1">Moderation Decision</label>
                      <div>
                        <Badge color="warning" className="px-2 py-1">{broadcast.moderation_decision}</Badge>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

          {/* Moderation Information */}
          {broadcast.moderation_reasons && broadcast.moderation_reasons.length > 0 && (
            <Card className="mb-3 shadow-sm border-danger">
              <CardBody className="p-3 bg-danger-subtle">
                <h6 className="mb-3 fw-bold text-danger">
                  <i className="mdi mdi-shield-alert me-2"></i>Moderation Reasons
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {broadcast.moderation_reasons.map((reason, index) => (
                    <Badge key={index} color="danger" className="px-2 py-1">
                      {reason}
                    </Badge>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Moderation Scores */}
          {broadcast.moderation_scores && (
            <Card className="mb-3 shadow-sm">
              <CardBody className="p-3">
                <h6 className="mb-3 fw-bold">
                  <i className="mdi mdi-chart-bar me-2"></i>Moderation Scores
                </h6>
                <Row className="g-3">
                  {broadcast.moderation_scores.Sexual !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Sexual</label>
                        <div>{broadcast.moderation_scores.Sexual}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_scores.Hate !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Hate</label>
                        <div>{broadcast.moderation_scores.Hate}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_scores["Harassment/Insult"] !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Harassment/Insult</label>
                        <div>{broadcast.moderation_scores["Harassment/Insult"]}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_scores.Violence !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Violence</label>
                        <div>{broadcast.moderation_scores.Violence}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_scores.Toxic !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Toxic</label>
                        <div>{broadcast.moderation_scores.Toxic}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_scores.intent && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Intent</label>
                        <div>
                          <Badge color="info" className="px-2 py-1">{broadcast.moderation_scores.intent}</Badge>
                        </div>
                      </div>
                    </Col>
                  )}
                  {broadcast.moderation_version && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Moderation Version</label>
                        <div className="small font-monospace">{broadcast.moderation_version}</div>
                      </div>
                    </Col>
                  )}
                  {broadcast.review_required !== undefined && (
                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">Review Required</label>
                        <div>
                          <Badge color={broadcast.review_required ? "danger" : "success"} className="px-2 py-1">
                            {broadcast.review_required ? "Yes" : "No"}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Broadcaster Information */}
          {broadcast.broadcaster && (
            <Card className="mb-3 shadow-sm">
              <CardBody className="p-3">
                <h6 className="mb-3 fw-bold">
                  <i className="mdi mdi-account me-2"></i>Broadcaster Information
                </h6>
                <Row className="g-3 align-items-center">
                  {broadcast.broadcaster.primaryImgURL && (
                    <Col xs="auto">
                      <img
                        src={broadcast.broadcaster.primaryImgURL}
                        alt={broadcast.broadcaster.username}
                        className="rounded-circle"
                        style={{ width: "60px", height: "60px", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/60";
                        }}
                      />
                    </Col>
                  )}
                  <Col>
                    <div className="d-flex align-items-center gap-2">
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{broadcast.broadcaster.username || "N/A"}</div>
                        <div className="text-muted small font-monospace">{broadcast.broadcaster.userId}</div>
                      </div>
                      {broadcast.broadcaster.userId && (
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleViewUserInfo(broadcast.broadcaster.userId)}
                          className="px-3"
                        >
                          <i className="mdi mdi-eye me-1"></i>View User
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button
            color="success"
            onClick={() => setApproveModal(true)}
            className="px-4"
          >
            <i className="mdi mdi-check-circle me-2"></i>Approve
          </Button>
          <Button
            color="danger"
            onClick={() => setRejectModal(true)}
            className="px-4"
          >
            <i className="mdi mdi-close-circle me-2"></i>Reject
          </Button>
          <Button
            color="warning"
            onClick={() => setSuspendModal(true)}
            className="px-4"
          >
            <i className="mdi mdi-pause-circle me-2"></i>Suspend
          </Button>
          <Button color="secondary" onClick={toggle} className="px-4">
            <i className="mdi mdi-close me-2"></i>Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal isOpen={approveModal} toggle={() => setApproveModal(false)} centered>
        <ModalHeader toggle={() => setApproveModal(false)} className="bg-success text-white">
          <i className="mdi mdi-check-circle me-2"></i>Approve Broadcast
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Are you sure you want to approve this broadcast?</p>
            {broadcast && (
              <div className="mt-2">
                <p className="mb-1"><strong>Title:</strong> {broadcast.title || "N/A"}</p>
                <p className="mb-0"><strong>Broadcast ID:</strong> {broadcast.broadcast_id}</p>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setApproveModal(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button color="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Approving...
              </>
            ) : (
              <>
                <i className="mdi mdi-check-circle me-2"></i>Approve
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)} centered>
        <ModalHeader toggle={() => setRejectModal(false)} className="bg-danger text-white">
          <i className="mdi mdi-close-circle me-2"></i>Reject Broadcast
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="alert alert-danger" role="alert">
              <i className="mdi mdi-alert-circle me-2"></i>
              <strong>Warning!</strong> This action cannot be undone.
            </div>
            {broadcast && (
              <div className="mb-3">
                <p className="mb-1">Are you sure you want to reject this broadcast?</p>
                <p className="mb-1"><strong>Title:</strong> {broadcast.title || "N/A"}</p>
                <p className="mb-0"><strong>Broadcast ID:</strong> {broadcast.broadcast_id}</p>
              </div>
            )}
            <FormGroup>
              <Label>Rejection Reason <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                rows="4"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                required
              />
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setRejectModal(false);
            setRejectReason("");
          }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="danger" 
            onClick={handleReject} 
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rejecting...
              </>
            ) : (
              <>
                <i className="mdi mdi-close-circle me-2"></i>Reject
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Suspend Confirmation Modal */}
      <Modal isOpen={suspendModal} toggle={() => setSuspendModal(false)} centered>
        <ModalHeader toggle={() => setSuspendModal(false)} className="bg-warning text-white">
          <i className="mdi mdi-pause-circle me-2"></i>Suspend Broadcast
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <p className="mb-0">Are you sure you want to suspend this broadcast?</p>
            {broadcast && (
              <div className="mt-2">
                <p className="mb-1"><strong>Title:</strong> {broadcast.title || "N/A"}</p>
                <p className="mb-0"><strong>Broadcast ID:</strong> {broadcast.broadcast_id}</p>
              </div>
            )}
            <FormGroup>
              <Label>Suspension Reason <span className="text-danger">*</span></Label>
              <Input
                type="textarea"
                rows="4"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Enter reason for suspension..."
                required
              />
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => {
            setSuspendModal(false);
            setSuspendReason("");
          }} disabled={actionLoading}>
            Cancel
          </Button>
          <Button 
            color="warning" 
            onClick={handleSuspend} 
            disabled={actionLoading || !suspendReason.trim()}
          >
            {actionLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Suspending...
              </>
            ) : (
              <>
                <i className="mdi mdi-pause-circle me-2"></i>Suspend
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh after user action
        }}
      />
    </>
  );
};

export default BroadcastDetailModal;

